import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Rolle, RolleAnlegen } from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, useBeforeUnload } from "@kundeninfo/ui";
import { KEINE_ZAHL, Meldung, alsZahl, feldFehlerAus, meldungAus } from "./gemeinsam.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich ANSPRECHPARTNER-ROLLEN.
 *
 * Herausgelöst aus views/Verwaltung.tsx (Etappe 2, Aufgabe 3) — rein strukturell, CSS und
 * Verhalten unverändert (Block `vw__`, views/Verwaltung.css).
 *
 * **Ohne zweite Ebene** (Entscheid Aufgabe 3, im Einklang mit der Spezifikation, die die
 * Rollen ausdrücklich als Formular-Bereich nennt): Eine Rolle trägt drei Felder — Name,
 * Reihenfolge, Aktiv —, und alle drei stehen bereits in ihrer Listenzeile. Eine Spaltenliste
 * davor brächte nichts zu sehen, was die Liste nicht schon zeigt, und nähme die eine
 * Auskunft weg, um die es hier geht: die REIHENFOLGE über alle Rollen hinweg.
 */

/** Nur die drei Wege dieses Bereichs — so lässt er sich im Test mit drei vi.fn zeichnen. */
export type RollenClient = Pick<ApiClient,
  "listContactRoles" | "createContactRole" | "updateContactRole">;

type RollenDialog = {
  id: string | null;
  version: number;
  name: string;
  sortIndex: string;
  active: boolean;
};

const LEERER_ROLLENDIALOG: RollenDialog =
  { id: null, version: 0, name: "", sortIndex: "0", active: true };

type RollenFeld = "name" | "sortIndex";

/** Wie konditionsdialogAus, für die Rollen. */
function rollendialogAus(r: Rolle | null): RollenDialog {
  if (r === null) return LEERER_ROLLENDIALOG;
  return { id: r.id, version: r.version, name: r.name, sortIndex: String(r.sortIndex),
           active: r.active };
}

function rollendialogGleich(a: RollenDialog, b: RollenDialog): boolean {
  return a.id === b.id && a.version === b.version && a.name === b.name
    && a.sortIndex === b.sortIndex && a.active === b.active;
}

export function RollenBereich({ client }: { client: RollenClient }): ReactElement {
  const [zeilen, setZeilen] = useState<Rolle[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<RollenDialog | null>(null);
  const [feldFehler, setFeldFehler] = useState<Partial<Record<RollenFeld, string>>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [arbeitet, setArbeitet] = useState(false);

  const listContactRoles = client.listContactRoles;

  useEffect(() => {
    let abgebrochen = false;
    listContactRoles()
      .then((antwort) => { if (!abgebrochen) setZeilen(antwort.items); })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(meldungAus(fehler, "Die Rollen konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [listContactRoles]);

  // 2a-Kleinpunkt, wie im Konditionsbereich.
  const dialogGeaendert = dialog !== null && !rollendialogGleich(
    dialog, rollendialogAus(zeilen?.find((z) => z.id === dialog.id) ?? null));
  useBeforeUnload(dialogGeaendert);

  /** Der eine Weg aus dem Modal neben dem Speichern (Hausregel „striktes Modalpanel",
   *  06.09.2026) — mit Rückfrage, wenn etwas Ungespeichertes darin steht. Escape ruft
   *  denselben Weg (siehe `Modal` in @kundeninfo/ui); ein Klick daneben schliesst nicht. */
  function abbrechen(): void {
    // Kein Ausgang, solange geschrieben wird — das gilt für die ESCAPE-Taste genauso wie
    // für den (dann gesperrten) Knopf: Beide gehen durch diese Funktion, und die Antwort
    // des Servers braucht ihr Panel noch (Fixrunde 1).
    if (arbeitet) return;
    if (!darfAbbrechen(dialogGeaendert)) return;
    setDialog(null);
  }

  function oeffnen(r: Rolle | null): void {
    setFeldFehler({});
    setMeldung(null);
    setDialog(rollendialogAus(r));
  }

  function eingabeAus(d: RollenDialog): RolleAnlegen | null {
    const sortIndex = alsZahl(d.sortIndex);
    if (sortIndex === KEINE_ZAHL || sortIndex === null) {
      setFeldFehler({ sortIndex: "Bitte eine Zahl eintragen." });
      return null;
    }
    return { name: d.name, sortIndex, active: d.active };
  }

  async function speichern(): Promise<void> {
    const d = dialog;
    if (d === null) return;
    setFeldFehler({});
    setMeldung(null);
    const eingabe = eingabeAus(d);
    if (eingabe === null) return;
    setArbeitet(true);
    try {
      if (d.id === null) {
        // Anlegen antwortet nur mit der Kennung — die Liste neu lesen (sie ist zudem
        // nach sortIndex geordnet, und der neue Eintrag gehört an seinen Platz).
        await client.createContactRole(eingabe);
        const antwort = await client.listContactRoles();
        setZeilen(antwort.items);
      } else {
        const neu = await client.updateContactRole(d.id, d.version, eingabe);
        setZeilen((alt) => (alt === null ? alt
          : alt.map((x) => (x.id === neu.id ? neu : x))
              .sort((a, b) => a.sortIndex - b.sortIndex)));
      }
      setDialog(null);
    } catch (fehler) {
      if (fehler instanceof ApiError && fehler.status === 422) {
        const { felder, unzuordenbar } = feldFehlerAus(fehler, ["name", "sortIndex"]);
        setFeldFehler(felder);
        if (unzuordenbar || Object.keys(felder).length === 0) setMeldung(fehler.message);
      } else {
        setMeldung(meldungAus(fehler, "Unerwarteter Fehler beim Speichern."));
      }
    } finally {
      setArbeitet(false);
    }
  }

  if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
  if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;

  // Die Liste kommt bereits nach sortIndex geordnet; hier wird sie es NOCH EINMAL, damit
  // die Reihenfolge auch nach einem Ändern ohne Neuladen stimmt.
  const sortiert = [...zeilen].sort((a, b) => a.sortIndex - b.sortIndex);

  return (
    <>
      {meldung !== null && dialog === null && <Meldung text={meldung} />}
      <div className="vw__leiste">
        <button type="button" className="vw__primaer" onClick={() => oeffnen(null)}>
          Neue Rolle
        </button>
      </div>

      <ul className="vw__liste">
        {sortiert.map((r) => (
          <li key={r.id} className="vw__eintrag">
            <div className="vw__eintrag-kopf">
              <span className="vw__eintrag-name">{r.name}</span>
              {!r.active && <span className="vw__marke">stillgelegt</span>}
            </div>
            <span className="vw__eintrag-zusatz">Reihenfolge {r.sortIndex}</span>
            <div className="vw__eintrag-aktionen">
              <button type="button" className="vw__knopf" aria-label={`${r.name} ändern`}
                onClick={() => oeffnen(r)}>
                Ändern
              </button>
            </div>
          </li>
        ))}
      </ul>

      {dialog !== null && (
        <Modal titel={dialog.id === null ? "Neue Rolle" : "Rolle ändern"}
          onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}>
          {meldung !== null && <Meldung text={meldung} />}
          <Feld id="vw-r-name" label="Name" bearbeitbar wert={dialog.name}
            onChange={(w) => setDialog((d) => (d === null ? d : { ...d, name: w }))}
            {...(feldFehler.name !== undefined ? { fehler: feldFehler.name } : {})} />
          <Feld id="vw-r-sortIndex" label="Reihenfolge" bearbeitbar wert={dialog.sortIndex}
            onChange={(w) => setDialog((d) => (d === null ? d : { ...d, sortIndex: w }))}
            {...(feldFehler.sortIndex !== undefined
              ? { fehler: feldFehler.sortIndex } : {})} />
          <label className="vw__schalter">
            <input type="checkbox" checked={dialog.active}
              onChange={(e) => setDialog((d) => (d === null ? d
                : { ...d, active: e.target.checked }))} />
            <span>Aktiv (steht zur Auswahl)</span>
          </label>
          <ModalFuss speichernText="Speichern" speichernGesperrt={arbeitet}
            abbrechenGesperrt={arbeitet}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </>
  );
}
